import React from 'react';
import { User, Calendar, Scale, Ruler, Percent } from 'lucide-react';

/**
 * Секция ввода основных параметров для расчета TDEE
 */
const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Система измерений */}
      <div className="col-span-full mb-2">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Система измерений
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="measurementSystem"
              value="metric"
              checked={formData.measurementSystem === 'metric'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Метрическая (кг, см)
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="measurementSystem"
              value="imperial"
              checked={formData.measurementSystem === 'imperial'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Имперская (фунты, дюймы)
            </span>
          </label>
        </div>
      </div>

      {/* Пол */}
      <div className="col-span-full">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Пол
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="gender"
              value="male"
              checked={formData.gender === 'male'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Мужской
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="gender"
              value="female"
              checked={formData.gender === 'female'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Женский
            </span>
          </label>
        </div>
      </div>

      {/* Возраст */}
      <div>
        <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-emerald-500" />
          Возраст (лет)
        </label>
        <input
          type="text"
          id="age"
          name="age"
          value={formData.age}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.age ? 'input-error' : ''}`}
          placeholder="Введите возраст"
        />
        {errors.age && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.age}
          </p>
        )}
      </div>

      {/* Вес */}
      <div>
        <label htmlFor="weight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Scale className="w-4 h-4 mr-2 text-emerald-500" />
          Вес ({formData.measurementSystem === 'metric' ? 'кг' : 'фунты'})
        </label>
        <input
          type="text"
          id="weight"
          name="weight"
          value={formData.weight}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.weight ? 'input-error' : ''}`}
          placeholder={`Введите вес в ${formData.measurementSystem === 'metric' ? 'кг' : 'фунтах'}`}
        />
        {errors.weight && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.weight}
          </p>
        )}
      </div>

      {/* Рост */}
      <div>
        <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Ruler className="w-4 h-4 mr-2 text-emerald-500" />
          Рост ({formData.measurementSystem === 'metric' ? 'см' : 'дюймы'})
        </label>
        <input
          type="text"
          id="height"
          name="height"
          value={formData.height}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.height ? 'input-error' : ''}`}
          placeholder={`Введите рост в ${formData.measurementSystem === 'metric' ? 'см' : 'дюймах'}`}
        />
        {errors.height && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.height}
          </p>
        )}
      </div>

      {/* Процент жира в организме (опционально) */}
      <div className="col-span-full">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeBodyFat"
            checked={formData.includeBodyFat}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Указать процент жира в организме (для более точного расчета)
          </span>
        </label>
      </div>

      {/* Поле ввода процента жира (если опция включена) */}
      {formData.includeBodyFat && (
        <div className="col-span-full md:col-span-1">
          <label htmlFor="bodyFat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-2 text-emerald-500" />
            Процент жира в организме (%)
          </label>
          <input
            type="text"
            id="bodyFat"
            name="bodyFat"
            value={formData.bodyFat}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.bodyFat ? 'input-error' : ''}`}
            placeholder="Например: 15"
          />
          {errors.bodyFat && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.bodyFat}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicParametersSection;